/**
 * 원격지원 파일 받기 — 받은 데이터를 쓰는 곳(remoteFileTransfer.ts의 FxSink 구현)
 *
 *  - PC 앱(직원 PC 메인 창·담당자 원격 창): Rust rs_file_* → 다운로드 폴더(src-tauri/src/remote_files.rs).
 *    경로는 Rust가 정하고 JS는 핸들만 들고 다닌다. 쓰기는 raw body(헤더 rs-handle)라 JSON 직렬화가 없다
 *  - 웹(담당자 브라우저): Blob으로 모았다가 끝나면 <a download>로 저장한다.
 *    16 MiB마다 Blob을 합쳐 Chromium이 큰 데이터를 디스크로 내보낼 수 있게 한다(ArrayBuffer로 쥐고 있지 않는다)
 */
import { isTauri } from '../utils/tauri';
import type { FxSink } from './remoteFileTransfer';
import type { FxFileMeta } from '../utils/remoteFileProtocol';

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown> | Uint8Array, headers?: Record<string, string>): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(cmd, args, headers ? { headers } : undefined);
}

/** Rust 오류 문자열 → 엔진이 알아보는 사유(disk_full 등은 그대로 실린다) */
function rustError(err: unknown): Error {
  return new Error(typeof err === 'string' ? err : String((err as Error)?.message || err));
}

/**
 * 다운로드 폴더에 쓴다. 직원 PC는 sessionId(진행 중인 세션과 일치해야 Rust가 연다),
 * 담당자 원격 창은 null(창 라벨로 판정)
 */
export async function openRustSink(file: FxFileMeta, sessionId: string | null): Promise<FxSink> {
  let handle: string;
  try {
    handle = await tauriInvoke<string>('rs_file_open', { sessionId, name: file.name, size: file.size });
  } catch (err) {
    throw rustError(err);
  }
  let closed = false;
  return {
    async write(data: Uint8Array) {
      try {
        await tauriInvoke<number>('rs_file_write', data, { 'rs-handle': handle });
      } catch (err) {
        throw rustError(err);
      }
    },
    async finish() {
      closed = true;
      try {
        const savedAs = await tauriInvoke<string | null>('rs_file_close', { handle, ok: true });
        return { savedAs: savedAs || file.name, revealId: handle };
      } catch (err) {
        throw rustError(err);
      }
    },
    async abort() {
      if (closed) return;
      closed = true;
      await tauriInvoke('rs_file_close', { handle, ok: false }).catch(() => {});
    },
  };
}

const BLOB_COMPACT_BYTES = 16 * 1024 * 1024;
/** 웹에서 받은 파일의 blob URL — 창을 닫을 때 해제한다 */
const blobUrls = new Map<string, { url: string; name: string }>();
let blobSeq = 0;

function triggerDownload(url: string, name: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** 웹: 다 받으면 브라우저 다운로드로 저장한다. revealId는 '다시 저장'에 쓰는 키 */
export function openBlobSink(file: FxFileMeta): FxSink {
  let blob = new Blob([]);
  let parts: Uint8Array[] = [];
  let partBytes = 0;
  const compact = () => {
    if (parts.length === 0) return;
    blob = new Blob([blob, ...(parts as BlobPart[])]);
    parts = [];
    partBytes = 0;
  };
  return {
    async write(data: Uint8Array) {
      parts.push(data);
      partBytes += data.byteLength;
      if (partBytes >= BLOB_COMPACT_BYTES) compact();
    },
    async finish() {
      compact();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/octet-stream' }));
      const key = `b${++blobSeq}`;
      blobUrls.set(key, { url, name: file.name });
      triggerDownload(url, file.name);
      return { savedAs: file.name, revealId: key };
    },
    async abort() {
      parts = [];
      blob = new Blob([]);
    },
  };
}

/** 받은 쪽에 맞는 싱크를 연다 */
export function openReceiveSink(file: FxFileMeta, sessionId: string | null): Promise<FxSink> {
  return isTauri() ? openRustSink(file, sessionId) : Promise.resolve(openBlobSink(file));
}

/** 받은 파일 다시 보기 — PC 앱: 탐색기에서 선택해 연다 / 웹: 다시 저장 */
export async function revealReceivedFile(revealId: string): Promise<void> {
  if (isTauri()) {
    await tauriInvoke('rs_file_reveal', { handle: revealId });
    return;
  }
  const b = blobUrls.get(revealId);
  if (!b) throw new Error('gone');
  triggerDownload(b.url, b.name);
}

/** 웹: 원격 창을 닫을 때 blob URL을 해제한다 */
export function releaseReceivedBlobs() {
  blobUrls.forEach(({ url }) => URL.revokeObjectURL(url));
  blobUrls.clear();
}
